'use client'

import {
    Catalog,
    CatalogCategory,
    getCatalog,
    getCatalogsByCompany,
} from '@/lib/api/catalog/catalog-api'
import { CatalogQuickCreate } from '@/components/catalog/catalog-quick-create'
import { useCatalogCategoryLabels } from '@/lib/hooks/use-service-label'
import { useAuth } from '@/store/hooks'
import { useEffect, useRef, useState } from 'react'
import { MdAdd, MdClose, MdInventory2, MdSearch } from 'react-icons/md'

interface Props {
    value: number | null
    onChange: (catalogId: number | null, catalog: Catalog | null) => void
    category?: CatalogCategory
    label?: string
    placeholder?: string
    disabled?: boolean
    className?: string
    /**
     * Busca sem resultado passa a oferecer "Adicionar <termo>" no dropdown,
     * abrindo o cadastro rápido. Exige `category` — o item precisa nascer com
     * uma categoria, e sem ela não há como adivinhar qual.
     */
    registerEnabled?: boolean
    /**
     * Parceiro dono do preço no cadastro rápido. Passe quando a busca desta tela
     * for funilada por parceiro: é o que permite atrelar um item existente em vez
     * de duplicá-lo.
     */
    registerPartnerId?: number | null
    registerPartnerName?: string
}

export function CatalogSelector({
    value,
    onChange,
    category,
    label = 'Item do catálogo',
    placeholder = 'Buscar item do catálogo...',
    disabled = false,
    className = '',
    registerEnabled = false,
    registerPartnerId,
    registerPartnerName,
}: Props) {
    const { user } = useAuth()
    const companyId = user?.company?.id
    const categoryLabels = useCatalogCategoryLabels()
    const [results, setResults] = useState<Catalog[]>([])
    const [loading, setLoading] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [showDropdown, setShowDropdown] = useState(false)
    const [selected, setSelected] = useState<Catalog | null>(null)
    const [showQuickCreate, setShowQuickCreate] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    // Carregar item já selecionado quando value muda externamente
    useEffect(() => {
        if (!value) {
            setSelected(null)
            return
        }
        if (selected?.id === value) return
        getCatalog(value)
            .then(res => {
                if (res.success && res.result) setSelected(res.result)
            })
            .catch(() => {})
    }, [value, selected?.id])

    // Click outside
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(e.target as Node)
            ) {
                setShowDropdown(false)
            }
        }
        if (showDropdown) document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [showDropdown])

    const doSearch = (term: string) => {
        if (debounceRef.current) clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(async () => {
            if (!companyId) return
            setLoading(true)
            try {
                const res = await getCatalogsByCompany(companyId, {
                    search: term || undefined,
                    category,
                    isListing: true,
                    page: 1,
                    limit: 20,
                })
                if (res.success)
                    setResults(Array.isArray(res.result) ? res.result : [])
            } catch {
                setResults([])
            } finally {
                setLoading(false)
            }
        }, 300)
    }

    const handleSearchChange = (term: string) => {
        setSearchTerm(term)
        setShowDropdown(true)
        doSearch(term)
    }

    const handleSelect = (catalog: Catalog) => {
        setSelected(catalog)
        onChange(catalog.id, catalog)
        setSearchTerm('')
        setShowDropdown(false)
    }

    const handleClear = () => {
        setSelected(null)
        onChange(null, null)
        setSearchTerm('')
    }

    // "Adicionar <termo>" só faz sentido com um termo digitado e uma categoria
    // para o item nascer. Sem categoria o cadastro rápido não teria o que criar.
    const canRegister =
        registerEnabled && !!category && searchTerm.trim().length > 0

    return (
        <div className={`form-control w-full ${className}`} ref={containerRef}>
            <label className="label">
                <span className="label-text">{label}</span>
            </label>

            {selected ? (
                <div className="flex items-center gap-2 p-2 bg-base-200 rounded-lg">
                    <MdInventory2 className="text-base-content/60" />
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                            {selected.name}
                        </p>
                        <p className="text-xs text-base-content/60">
                            {categoryLabels[selected.category] ||
                                selected.category}
                            {selected.tussCode
                                ? ` · TUSS ${selected.tussCode}`
                                : ''}
                        </p>
                    </div>
                    <button
                        type="button"
                        className="btn btn-ghost btn-xs btn-circle"
                        onClick={handleClear}
                        disabled={disabled}
                        aria-label="Limpar"
                    >
                        <MdClose />
                    </button>
                </div>
            ) : (
                <div className="relative">
                    <div className="relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/40" />
                        <input
                            type="text"
                            className="input input-bordered w-full pl-10"
                            placeholder={placeholder}
                            value={searchTerm}
                            onChange={e => handleSearchChange(e.target.value)}
                            onFocus={() => {
                                setShowDropdown(true)
                                if (!results.length) doSearch('')
                            }}
                            disabled={disabled}
                        />
                    </div>
                    {showDropdown && (
                        <div className="absolute z-50 w-full mt-1 bg-base-100 border border-base-300 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                            {loading && (
                                <div className="p-3 text-center text-sm text-base-content/60">
                                    <span className="loading loading-spinner loading-xs mr-2" />
                                    Buscando...
                                </div>
                            )}
                            {!loading && results.length === 0 && (
                                <div className="p-3 text-center text-sm text-base-content/60">
                                    Nenhum item encontrado
                                </div>
                            )}
                            {!loading && results.length === 0 && canRegister && (
                                <button
                                    type="button"
                                    className="w-full text-left p-3 hover:bg-base-200 border-t border-base-300/50 text-secondary font-medium text-sm flex items-center gap-2"
                                    onClick={() => {
                                        setShowDropdown(false)
                                        setShowQuickCreate(true)
                                    }}
                                >
                                    <MdAdd className="text-lg flex-shrink-0" />
                                    <span className="truncate">
                                        Adicionar "{searchTerm.trim()}"
                                    </span>
                                </button>
                            )}
                            {!loading &&
                                results.map(c => (
                                    <button
                                        key={c.id}
                                        type="button"
                                        className="w-full text-left p-3 hover:bg-base-200 border-b border-base-300/50 last:border-b-0"
                                        onClick={() => handleSelect(c)}
                                    >
                                        <span className="font-medium text-sm">
                                            {c.name}
                                        </span>
                                        <span className="text-xs text-base-content/60 ml-2">
                                            {categoryLabels[c.category] ||
                                                c.category}
                                        </span>
                                    </button>
                                ))}
                        </div>
                    )}
                </div>
            )}

            {showQuickCreate && category && companyId && (
                <CatalogQuickCreate
                    open
                    onClose={() => setShowQuickCreate(false)}
                    initialName={searchTerm}
                    category={category}
                    companyId={companyId}
                    partnerId={registerPartnerId}
                    partnerName={registerPartnerName}
                    requireTuss={category === 'PROCEDURE'}
                    onReady={handleSelect}
                />
            )}
        </div>
    )
}
